// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Select, TextField, Box, MenuItem, InputAdornment } from '@mui/material';

const EXCLUDED_ATTRIBUTE_PREFIXES = ['Failure', 'AllPredeterminedProjectsDone', 'QuasiFailure', 'Operating'];

export const MultiInputField = ({
  value: currentValue,
  onChange,
  options = [],
  selectedAssetClassAttrsList = [],
  attributDeclencheur,
  disabled = false,
  label,
}) => {
  const [selectedType, setSelectedType] = useState(() => {
    if (currentValue && typeof currentValue === 'string') {
      if (currentValue.includes('AGE REF DELTA')) return 'Age Ref Delta';
      if (currentValue.includes('AGE REF POURCENTAGE')) return 'Age Ref Pourcentage';
      if (currentValue.includes('CustomDataNumber')) return 'CustomDataNumber';
      return 'ScalarValue';
    }
    return 'ScalarValue';
  });

  const customDataNumberOptions = useMemo(() => {
    return (selectedAssetClassAttrsList || []).filter((attr) => attr.startsWith('CustomDataNumber'));
  }, [selectedAssetClassAttrsList]);

  const getAvailableOptions = (attributDeclencheur) => {
    if (attributDeclencheur?.startsWith('Age') || attributDeclencheur?.startsWith('State')) {
      return ['ScalarValue', 'CustomDataNumber', 'Age Ref Delta', 'Age Ref Pourcentage'];
    }
    if (attributDeclencheur === 'InspectionDowntime' || attributDeclencheur === 'Downtime') {
      return ['ScalarValue'];
    }
    return ['ScalarValue', 'CustomDataNumber'];
  };

  const filteredOptions = options.filter((option) => {
    const availableOptions = getAvailableOptions(attributDeclencheur);

    if (availableOptions) {
      return availableOptions.includes(option.value ?? option);
    }

    const isExcluded = EXCLUDED_ATTRIBUTE_PREFIXES.some((prefix) => attributDeclencheur?.startsWith(prefix));
    return !isExcluded;
  });

  const getDisplayValue = () => {
    if (selectedType === 'Age Ref Delta' && currentValue) {
      return currentValue.replace('AGE REF DELTA ', '');
    }
    if (selectedType === 'Age Ref Pourcentage' && currentValue) {
      return currentValue.replace('AGE REF POURCENTAGE ', '');
    }
    return currentValue || '';
  };

  const handleTypeChange = (newType) => {
    setSelectedType(newType);
    onChange('');
  };

  useEffect(() => {
    const availableTypes = filteredOptions.map((option) => option.value ?? option);
    const isCurrentTypeValid = availableTypes.includes(selectedType);
    const isExcluded = EXCLUDED_ATTRIBUTE_PREFIXES.some((prefix) => attributDeclencheur?.startsWith(prefix));

    if (isExcluded) {
      setSelectedType('');
      onChange('');
      return;
    }

    if (!isCurrentTypeValid) {
      const firstAvailableOption = filteredOptions[0];
      if (firstAvailableOption) {
        const firstValue = firstAvailableOption.value ?? firstAvailableOption;
        setSelectedType(firstValue);
        onChange('');
      }
    }
  }, [attributDeclencheur, selectedType, filteredOptions, onChange]);

  const handleValueChange = (inputValue) => {
    if (selectedType === 'Age Ref Delta') {
      onChange(`AGE REF DELTA ${inputValue}`);
    } else if (selectedType === 'Age Ref Pourcentage') {
      onChange(`AGE REF POURCENTAGE ${inputValue}`);
    } else {
      onChange(inputValue);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: '100%', height: '100%' }}>
      {filteredOptions.length > 1 && (
        <Select
          sx={{ minHeight: '56px' }}
          value={selectedType}
          displayEmpty={true}
          data-testid={label}
          data-cy="valeur-reference-select"
          onChange={(e) => handleTypeChange(e.target.value)}
          fullWidth
          size="small"
          disabled={disabled}
        >
          {filteredOptions.map((option) => (
            <MenuItem
              key={option.value ?? option}
              data-cy={option.value || option.label || option}
              value={option.value ?? option}
            >
              {option.label ?? option}
            </MenuItem>
          ))}
        </Select>
      )}

      {selectedType === 'CustomDataNumber' ? (
        <Select
          key="additional_input"
          sx={{ minHeight: '56px' }}
          value={getDisplayValue()}
          displayEmpty={true}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          size="small"
          disabled={disabled}
        >
          {customDataNumberOptions.map((attr) => {
            const label = attr.includes('.') ? attr.split('.').pop() : attr;
            return (
              <MenuItem key={attr} value={attr}>
                {label}
              </MenuItem>
            );
          })}
        </Select>
      ) : (
        <TextField
          key="additional_input"
          data-cy="valeur-reference-input"
          sx={{
            '& .MuiInputBase-root': {
              minHeight: '56px',
            },
            '& .MuiOutlinedInput-root': {
              minHeight: '56px',
            },
          }}
          value={getDisplayValue()}
          type="number"
          placeholder=""
          InputProps={{
            inputProps: {
              step: 'any',
            },
            endAdornment:
              attributDeclencheur === 'Downtime' || attributDeclencheur === 'InspectionDowntime' ? (
                <InputAdornment position="end">an(s)</InputAdornment>
              ) : null,
          }}
          onChange={(e) => handleValueChange(e.target.value)}
          fullWidth
          size="small"
          disabled={disabled}
        />
      )}
    </Box>
  );
};

MultiInputField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array,
  selectedAssetClassAttrsList: PropTypes.array,
  attributDeclencheur: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
};

export default MultiInputField;
